import { useTranslation } from 'react-i18next';

import CompanyDataCard, { CompanyDataRow, SummarySmallBlock } from 'components/reusable/CompanyDataCard';
import CollapsibleTable from 'components/reusable/CollapsibleTable';
import useIsUiHidden from 'hooks/dom/useIsUiHidden';
import useFormatDate from 'hooks/format/useFormatDate';

import { Col, Row, Card } from 'antd';
import { ReactComponent as BailifsIcon } from 'assets/img/icons/cardIcons/bailifs.svg';
import { useSelector } from 'react-redux';
import { negativeDataBailiffsInformation } from '../../../../store/entities/professionalReport/selectors/negativeDataSelectors';
import TableColumnDate from '../../../../components/reusable/TableColumnDate';
import useNumberFormatter from '../../../../hooks/format/useNumberFormatter';
import ExpandableButton from '../../../../components/reusable/ExpandableButton';
import React, { useState } from 'react';
import { isOlderThanXYear } from '../../../../helpers/common';

const BailiffsInformation = () => {
  const { t } = useTranslation();
  const formatDate = useFormatDate();
  const isUiHidden = useIsUiHidden();
  const formatNumber = useNumberFormatter({ style: 'decimal' });
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const handleExpand = (record, e) => {
    e.stopPropagation();
    setExpandedRowKeys(prev =>
      prev.includes(record.creditinfoId)
        ? prev.filter(creditinfoId => creditinfoId !== record.creditinfoId)
        : [...prev, record.creditinfoId]
    );
  };

  const { bailiffsRemainders, bailiffsRemaindersDetails } = useSelector(negativeDataBailiffsInformation);

  const bailiffsInformationColumns = [
    {
      title: t('professionalReport.negativeData.bailiffsInformation.reminderDate'),
      dataIndex: 'reminderDate',
      key: 'reminder_date',
      render: TableColumnDate,
    },
    {
      title: t('professionalReport.negativeData.bailiffsInformation.recoverableAmount'),
      dataIndex: 'recoverableAmount',
      key: 'amount',
      render: amount => {
        return formatNumber(amount);
      },
    },
    {
      title: t('professionalReport.negativeData.bailiffsInformation.plaintiffName'),
      dataIndex: 'plaintiffName',
      key: 'plaintiff',
    },
    {
      title: t('professionalReport.negativeData.bailiffsInformation.caseNumber'),
      dataIndex: 'caseNumber',
      key: 'case_no',
    },
    ...(!isUiHidden
      ? [
          {
            width: 120,
            key: 'creditinfoId',
            render: (_, record) => {
              return record?.bailiffName || record?.institutionName ? (
                <ExpandableButton
                  withText
                  isActive={expandedRowKeys.includes(record.creditinfoId)}
                  onClick={e => handleExpand(record, e)}
                />
              ) : null;
            },
          },
        ]
      : []),
  ];

  const expandedRow = record => {
    return (
      <Row gutter={[32]}>
        <Col span={12}>
          <div>
            <b>{`${t('professionalReport.negativeData.bailiffsInformation.details.bailiff')}: `}</b>
            {record.bailiffName}
          </div>
          <div>
            <b>{`${t('professionalReport.negativeData.bailiffsInformation.details.settlementPeriod')}: `}</b>
            {record.settlementPeriod}
          </div>
          <div>
            <b>{`${t('professionalReport.negativeData.bailiffsInformation.details.institutionName')}: `}</b>
            {record.institutionName}
          </div>
          <div>
            <b>{`${t('professionalReport.negativeData.bailiffsInformation.details.executiveDocumentName')}: `}</b>
            {record.executiveDocumentName}
          </div>
          <div>
            <b>{`${t('professionalReport.negativeData.bailiffsInformation.details.executiveDocumentDate')}: `}</b>
            {record?.executiveDocumentDate ? formatDate(record.executiveDocumentDate) : '-'}
          </div>
        </Col>
        <Col span={12}>
          <div>
            <b>{`${t('professionalReport.negativeData.bailiffsInformation.details.executiveDocumentNumber')}: `}</b>
            {record.executiveDocumentNumber}
          </div>
          <div>
            <b>{`${t('professionalReport.negativeData.bailiffsInformation.details.source')}: `}</b>
            <a href={record.source} target="_blank">
              {record.source}
            </a>
          </div>
          <div>
            <b>{`${t('professionalReport.negativeData.bailiffsInformation.details.note')}: `}</b>
            {t('professionalReport.negativeData.bailiffsInformation.note')}
          </div>
        </Col>
      </Row>
    );
  };

  return (
    <>
      <CompanyDataCard
        icon={<BailifsIcon />}
        title={t('professionalReport.negativeData.bailiffsInformation.bailiffsCalls.title')}
        style={{ background: '#f4f4f4' }}
        className={
          !!bailiffsRemainders?.total && !isOlderThanXYear(bailiffsRemainders?.latestDate, 1) ? 'negative-class' : ''
        }
      >
        {bailiffsRemaindersDetails.length ? (
          <Row gutter={isUiHidden ? [3] : [16, 16]} className="ant-row-pdf-gap">
            <SummarySmallBlock
              icon="baillifsCount"
              title={t('startReport.negativeData.bailiffsInformation.bailiffs.count')}
              value={bailiffsRemainders?.total ?? '-'}
            />
            <SummarySmallBlock
              title={t('startReport.negativeData.bailiffsInformation.bailiffs.date')}
              value={formatDate(bailiffsRemainders?.latestDate) ?? '-'}
              icon="negativeCalendar"
            />
          </Row>
        ) : (
          <Row>
            <Card className="card-transparent space-top-md">
              {t('professionalReport.negativeData.bailiffsInformation.bailiffsCalls.noInformation')}
            </Card>
          </Row>
        )}
        {bailiffsRemaindersDetails?.length ? (
          <CollapsibleTable
            rowKey="creditinfoId"
            className="space-top-md"
            title={t('professionalReport.negativeData.openDebts.detailedInformation.title')}
            data={bailiffsRemaindersDetails}
            columns={bailiffsInformationColumns}
            expandable={{
              expandedRowRender: record => expandedRow(record),
              rowExpandable: record => record?.bailiffName || record?.institutionName,
              defaultExpandAllRows: isUiHidden,
              expandIconColumnIndex: -1,
              expandedRowKeys,
              onExpandedRowsChange: setExpandedRowKeys,
            }}
          />
        ) : null}
      </CompanyDataCard>
    </>
  );
};

export default BailiffsInformation;
